import './Author.scss';
import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import Navbar from "../../components/Navbar/Navbar";
import Footer from "../../components/Footer/Footer";
import Book from "../../components/Book/Book";
import { API_BASE_URL } from '../../config';
import { formatDate } from '../../utils/dates';
import { COUNTRY_BY_CODE } from '../Profile/countries';

// Public writer page: what a reader sees when they tap an author's name.
// Only published, non-anonymous books are here; the API 404s for anyone
// without at least one, so this page never confirms that an account exists.

const plural = (n, one, few, many) => (n === 1 ? one : n >= 3 && n <= 10 ? `${n.toLocaleString('ar-EG')} ${few}` : `${n.toLocaleString('ar-EG')} ${many}`);

export default function Author() {
    const { username } = useParams();
    const [data, setData] = useState(null);
    const [state, setState] = useState('loading');   // loading | ok | missing | failed

    useEffect(() => {
        let cancelled = false;
        setState('loading'); setData(null);
        (async () => {
            try {
                const r = await fetch(`${API_BASE_URL}/api/books/author/${encodeURIComponent(username)}/`);
                if (r.status === 404) { if (!cancelled) setState('missing'); return; }
                if (!r.ok) throw new Error(`HTTP ${r.status}`);
                const d = await r.json();
                if (!cancelled) { setData(d); setState('ok'); }
            } catch {
                if (!cancelled) setState('failed');
            }
        })();
        return () => { cancelled = true; };
    }, [username]);

    useEffect(() => {
        document.title = `${username} · راوية`;
        return () => { document.title = 'راوية'; };
    }, [username]);

    const a = data?.author;
    const country = a?.country ? COUNTRY_BY_CODE[a.country] || a.country : '';

    return (
        <>
        <Navbar />
        <main className='author-page'>
            <div className='author-container'>
                {state === 'loading' && <p className='author-empty'>جارٍ التحميل…</p>}
                {state === 'failed' && <p className='author-empty'>تعذر تحميل صفحة الكاتب.</p>}
                {state === 'missing' && (
                    <div className='author-empty'>
                        <p>لا يوجد كاتب بهذا الاسم، أو لم ينشر شيئًا باسمه بعد.</p>
                        <Link to='/authors' className='author-back'>تصفّح الكتّاب</Link>
                    </div>
                )}

                {state === 'ok' && a && (
                    <>
                    <header className='author-header'>
                        <div className='author-avatar' aria-hidden='true'>{a.username.slice(0, 1).toUpperCase()}</div>
                        <div className='author-id'>
                            <h1 className='author-name'>{a.username}</h1>
                            <p className='author-meta'>
                                {country && <span>{country}</span>}
                                <span>انضم {formatDate(a.joined)}</span>
                                <span>{plural(a.book_count, 'كتاب واحد', 'كتب', 'كتابًا')}</span>
                                <span>{a.total_views.toLocaleString('ar-EG')} قراءة</span>
                            </p>
                            {a.bio && <p className='author-bio'>{a.bio}</p>}
                        </div>
                    </header>

                    <section className='author-books'>
                        <h2>كتب {a.username}</h2>
                        <div className='author-grid'>
                            {data.books.map(book => (
                                <Book
                                    key={book.id}
                                    id={book.id}
                                    title={book.title}
                                    author={book.author}
                                    displayAuthor={book.display_author}
                                    coverImage={book.cover_image}
                                    categoryLabel={book.category_label}
                                    is_published={book.is_published}
                                    publishedAt={book.published_at}
                                    updatedAt={book.date_modified}
                                    avgRating={book.avg_rating}
                                    ratingCount={book.rating_count}
                                    viewCount={book.view_count}
                                    showBadge={false}
                                    to={`/read/${book.id}`}
                                />
                            ))}
                        </div>
                    </section>
                    </>
                )}
            </div>
        </main>
        <Footer />
        </>
    );
}
